import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Picker } from '@react-native-picker/picker';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Colors from '../../constants/Colors';
import Languages from '../../constants/Languages';

const styles = StyleSheet.create({
  container: {
    // height: Platform.OS == 'web' ? 50 : 65,
    minHeight: Platform.OS == 'web' ? 54 : 65,
    alignItems: 'flex-start',
    width: '100%',
  },
  requiredContiner: {
    flexDirection: 'row',
  },
  requiredTextStyle: {
    color: Colors.brightRed,
    fontWeight: 'bold',
  },
  requiredHelperStyle: {
    fontSize: 10,
  },
  dropdown: {
    width: '100%',
    // add a background color to trigger the dropdown arrow for ios > web
    backgroundColor: Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.01)' : '#00000000',
    justifyContent: 'space-between',
    fontSize: 16,
    color: Colors.veryDarkGray,
  },
  label: {
    textAlign: 'left',
    width: '100%',
    marginBottom: 10,
  },
  header: {
    backgroundColor: Colors.mediumBlue,
  },
  itemStyle: {
    height: 126,
  },
  icon: {
    color: '#fff',
  },
  title: {
    color: '#fff',
  },
  body: {
    flex: 1,
  },
});

/**
 * `Selector` is a functional component that renders a dropdown selector with various options.
 * The options for the selector can be dynamically generated based on the provided dataset.
 * Additionally, extra options can be manually added. The selector also includes a label and,
 * if required, a required field indicator.
 *
 * @component
 * @param {Object} props - Props for the Selector component.
 * @param {string} props.label - The label for the selector.
 * @param {Function} props.onChange - Callback function that is called when the selected value changes.
 * @param {any} props.currentValue - The currently selected value.
 * @param {string} props.dataset - The dataset used to generate the dropdown options.
 * @param {Array} [props.extraOptions] - Optional extra options to be added to the dropdown.
 * @param {Object} props.translation - Object containing translation strings.
 * @param {Object} [props.dropdownStyle] - Optional styles for the dropdown.
 * @param {boolean} [props.isRequired] - Indicates if the selector field is required.
 * @param {string} [props.testID] - Test ID for the selector
 *
 * @returns {React.ReactElement} A React Element representing the custom selector dropdown.
 */
function Selector(props) {
  const {
    label,
    onChange,
    currentValue,
    dataset,
    field,
    extraOptions, // optional, if provided (value, name) add to dropdown options
    translation,
    dropdownStyle,
    isRequired,
    testID,
  } = props;

  const [dropdownValues, setDropdownValues] = useState([]);

  useEffect(() => {
    const options = [<Picker.Item key={`select-one`} label={translation.select} value="" />];
    const keys = Object.keys(translation);

    switch (dataset) {
      case 'state':
        Languages.states.forEach((item, index) => {
          options.push(
            <Picker.Item key={`state-${item.value}-${index}`} label={item.value} value={item.value} />
          );
        });
        break;
      default:
        keys.forEach((key) => {
          if (key.indexOf(`${dataset}_option`) !== -1) {
            options.push(
              <Picker.Item
                key={key}
                label={translation[key]}
                value={(key + '').replace(`${dataset}_option_`, '')}
              />
            );
          }
        });
        break;
    }
    if (extraOptions && extraOptions.length) {
      extraOptions.forEach((item, index) => {
        options.push(
          <Picker.Item key={`extra-${item.value}-${index}`} label={item.name} value={item.value} />
        );
      });
    }
    setDropdownValues(options);
  }, [dataset]);

  let selectedValue = '';
  if (currentValue !== null && currentValue !== undefined) {
    selectedValue = currentValue;
  }

  const labelStyles = [styles.label, ...(isRequired ? [styles.requiredTextStyle] : [])];

  return (
    <View style={styles.container}>
      <Text style={labelStyles}>{isRequired ? `${label}*` : label}</Text>
      <Picker
        mode="dropdown"
        selectedValue={selectedValue}
        placeholder={label}
        onValueChange={(value) => onChange(field, value)}
        style={[styles.dropdown, dropdownStyle || {}]}
        itemStyle={styles.itemStyle}
        testID={testID}
      >
        {dropdownValues}
      </Picker>
      {isRequired && (
        <View style={styles.requiredContiner}>
          <Text style={[styles.requiredTextStyle, styles.requiredHelperStyle]}>
            {translation.form_required}
          </Text>
        </View>
      )}
    </View>
  );
}

Selector.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  currentValue: PropTypes.any,
  dataset: PropTypes.string,
  extraOptions: PropTypes.array,
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  dropdownStyle: PropTypes.objectOf(PropTypes.any),
  testID: PropTypes.string,
};

export default Selector;
